import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import IdentificationTagsService from 'api/identificationTags/IdentificationTagsService';
import Logger from 'common/services/Logger';
import { Paged } from 'api/common/types/Page';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce';
import { IdentificationTagsDto } from 'api/identificationTags/models/IdentificationTagsDto';
import { IdentificationTagsSearchCriteria } from 'api/identificationTags/models/IdentificationTagsSearchCriteria';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import { FaEllipsisH } from 'react-icons/fa';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Popover from 'common/components/popover/Popover';
import IdentificationTagsFiltersScreen, { Filters } from '../identificationTagsFilters/IdentificationTagsFiltersScreen';
import { useHistory } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import styles from './IdentificationTagList.module.scss';
import UsersService from 'api/users/UsersService';

type Props = {
};

const IdentificationTagList: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const history = useHistory();

    const [IdentificationTagsPage, setIdentificationTagsPage] = useState<Paged<IdentificationTagsDto>>();
    const [criteria, setCriteria] = useState<IdentificationTagsSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'it.code',
        orderBy: 'asc'
    });

    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<IdentificationTagsDto | null>(null);

    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);

    const isFirstLoad = useRef(true);
    const filterJsonKey = 'PARAMETERS_IDENTIFICATION_TAGS_FILTER';

    const showRemoveItemDialog = (item: IdentificationTagsDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasIdentificationTagsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_IDENTIFICATION_TAGS_READ']);

    const renderTableActionCell = (row: IdentificationTagsDto) => {
        return <Dropdown
            options={
                <>
                    <DropdownItem url={`/settings/identificationTags/details/${row.id ?? ''}`}>
                        {t('common.details')}
                    </DropdownItem>
                    {hasIdentificationTagsWritePolicy && <DropdownItem url={`/settings/identificationTags/edit/${row.id ?? ''}`}>
                        {t('common.edit')}
                    </DropdownItem>}
                    {hasIdentificationTagsWritePolicy && <DropdownItem onClick={() => showRemoveItemDialog(row)}>
                        {t('common.remove')}
                    </DropdownItem>}
                </>
            }
        >
            <div><FaEllipsisH /></div>
        </Dropdown>
    }

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<IdentificationTagsDto>[] = [
        {
            field: 'code',
            name: t('identification_tags.code'),
            onSearch: onTableFilter,
            searchField: 'it.code'
        },
        {
            field: 'userName',
            name: t('identification_tags.user'),
            onSearch: onTableFilter,
            searchField: 'u.real_name'
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' }
        }
    ];

    const getData = async () => {
        try {
            Loading.show();

            saveOrdernationFilters();

            const page = await IdentificationTagsService.getList(criteria);
            setIdentificationTagsPage(page);

            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get identification tag list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
        }
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await IdentificationTagsService.remove(itemToRemove);
            setCriteria({ ...criteria, page: 1 });
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete identification tags`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    useEffect(() => {
        if (isFirstLoad.current == true) {
            loadFilters()
        } else {
            getData()
        }
    }, [criteria]);

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setCriteria({ ...criteria, page: 1, allIn: value });
    }, 500);

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        newFilters.allIn = criteria.allIn;
        newFilters.page = criteria.page;
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);
    }

    const onChangeFilters = (f: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(f);
        saveFiltersOnCache(f);
        setCriteria(prev => ({
            ...prev,
            ...f,
            page: (isOnLoad && f.page) ? f.page : 1,
        }))
    }

    const updateTotalFilters = (f: Filters) => {
        let total = 0;

        if (f.userId && f.userId.length > 0) {
            total++;
        }

        setFiltersTotal(total);
    }

    const updateFilters = (items: { key: string; value: any }[]) => {
        const newFilters = JSON.parse(JSON.stringify(filters))
        items.forEach((item: { key: string; value: any }) => {
            newFilters[item.key] = item.value
        })

        onChangeFilters(newFilters)
    }

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(prev => ({ ...prev, ...f }))
        localStorage.setItem(filterJsonKey, JSON.stringify(f));
    }

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(filterJsonKey);
        if (cachedFilters != null) { onChangeFilters(JSON.parse(cachedFilters), true) }
        else { getData() }

        isFirstLoad.current = false;
    }

    return (
        <div className='list'>
            <div className={styles.tabHeader}>
                <div>
                    <InputSearch onChangeValue={debounced} defaultValue={criteria.allIn} placeholder={t('identification_tags.list.search_placeholder')} ></InputSearch>
                </div>
                <div className={styles.rowFlex}>
                    <Popover
                        contentContainerClassName={styles.filtersPopoverContainer}
                        containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                        positions={['bottom', 'left']}
                        align={'end'}
                        onClickOutside={() => updateTotalFilters(filters)}
                        content={setIsPopoverOpen => <IdentificationTagsFiltersScreen
                            filters={filters}
                            onFilter={filters => { setIsPopoverOpen(false); onChangeFilters(filters) }}
                            onChange={filters => updateTotalFilters(filters)}
                        />}>
                        {(isPopoverOpen, setIsPopoverOpen) => (
                            <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                {filtersTotal > 0 &&
                                    <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                }
                            </ScreenHeaderButton>
                        )}
                    </Popover>
                    {hasIdentificationTagsWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => history.push(`/settings/identificationTags/new`)} />}
                </div>
            </div>
            { isFirstLoad.current == false && <ListingTable
                columns={tableColumns}
                rows={IdentificationTagsPage?.items || []}
                onRowClick={row => history.push(`/settings/identificationTags/details/${row.id ?? ''}`)}
                onHref={row => `/settings/identificationTags/details/${row.id ?? ''}`}
                allowHover={true}
                initialSearch={{ colField: criteria.orderColumn ?? 'u.real_name', isOrderAsc: (criteria.orderBy && criteria.orderBy == 'asc') ? true : false }}
            />}
            <PaginationWithInfo
                itemName={t('identification_tags.list.title')}
                currentPage={IdentificationTagsPage?.currentPage || 1}
                pageItems={IdentificationTagsPage?.items.length || 0}
                totalItems={IdentificationTagsPage?.totalItems || 0}
                onChange={page => setCriteria({ ...criteria, page })}
            />
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_record_with_ident', { name: itemToRemove?.code ?? '' })} />
        </div>
    );
};

export default IdentificationTagList;
